import React, { useEffect, useMemo, useState } from "react";
import API from "../../services/api";
import targetApi from "../../services/targetApi";
import DataTable from "../../components/common/DataTable";
import KpiCard from "../../components/target/KpiCard";
import { useToast } from "../../components/common/Toast";
import { FaPhone, FaDownload, FaCheckCircle, FaTimesCircle, FaClock } from "react-icons/fa";
import "../../styles/target.css";
import "../../styles/adminGlobal.css";

/* =========================================================
   Admin: Sales Call Logs
   Read-only, company-wide view of every call log entry
   filled by Telesales / Sales Executive employees.

   Reuses the existing (already role-aware) endpoint:
     GET /api/calllogs
   -> when the logged-in user is Boss/Admin, the backend
      returns every call log across all employees, joined
      with the employee's name (see callController.getCalls).

   Styled the same way as the other admin screens (Target
   Achievements / Global Data Explorer) so it fits right in
   next to them on the Boss sidebar.
========================================================= */

const parseMySQLDate = (dateVal) => {
  if (!dateVal) return new Date();
  if (dateVal instanceof Date) return dateVal;
  const str = String(dateVal);
  const cleaned = str
    .replace("T", " ")
    .replace(/\.\d+/, "")
    .replace(/Z$/, "")
    .replace(/[+-]\d{2}:\d{2}$/, "");
  return new Date(cleaned);
};

const toISTDisplay = (dateVal) => {
  const d = parseMySQLDate(dateVal);
  const day = String(d.getDate()).padStart(2, "0");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[d.getMonth()];
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  return `${day} ${month} ${year}, ${String(hours).padStart(2, "0")}:${minutes} ${ampm}`;
};

const toDateString = (dateVal) => {
  const d = parseMySQLDate(dateVal);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const STATUSES = ["Interested", "Not Interested", "Followup", "Closed", "No Answer"];

function SalesCallLogs() {
  const toast = useToast();

  const [logs, setLogs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [page, setPage] = useState(1);
  const limit = 15;

  const fetchLogs = () => {
    setLoading(true);
    API.get("/calllogs")
      .then((res) => setLogs(res.data || []))
      .catch(() => toast.error("Failed to load call logs"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLogs();
    targetApi
      .getTargetableUsers()
      .then((res) => setEmployees(res.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= FILTER (client-side) ================= */
  const filteredLogs = useMemo(() => {
    let result = [...logs];

    if (employeeId) {
      result = result.filter((l) => String(l.user_id) === String(employeeId));
    }
    if (status) {
      result = result.filter((l) => l.call_status === status);
    }
    if (dateFrom) {
      result = result.filter((l) => toDateString(l.call_date) >= dateFrom);
    }
    if (dateTo) {
      result = result.filter((l) => toDateString(l.call_date) <= dateTo);
    }
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (l) =>
          (l.customer_name || "").toLowerCase().includes(q) ||
          (l.phone || "").includes(q) ||
          (l.source || "").toLowerCase().includes(q) ||
          (l.employee_name || "").toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => parseMySQLDate(b.call_date) - parseMySQLDate(a.call_date));
    return result;
  }, [logs, employeeId, status, dateFrom, dateTo, search]);

  /* ================= STATS ================= */
  const stats = useMemo(
    () => ({
      total: filteredLogs.length,
      interested: filteredLogs.filter((l) => l.call_status === "Interested").length,
      notInterested: filteredLogs.filter((l) => l.call_status === "Not Interested").length,
      followup: filteredLogs.filter((l) => l.call_status === "Followup").length,
    }),
    [filteredLogs]
  );

  /* ================= PAGINATION (client-side) ================= */
  const pagedLogs = useMemo(() => {
    const start = (page - 1) * limit;
    return filteredLogs.slice(start, start + limit);
  }, [filteredLogs, page]);

  useEffect(() => setPage(1), [employeeId, status, dateFrom, dateTo, search]);

  /* ================= EXPORT ================= */
  const handleExport = () => {
    const headers = ["Employee", "Name", "Phone", "Source", "Status", "Packages", "Notes", "Date"];
    const rows = filteredLogs.map((l) => [
      l.employee_name || "",
      l.customer_name || "",
      l.phone || "",
      l.source || "",
      l.call_status || "",
      l.packages || "",
      (l.notes || "").replace(/,/g, ";"),
      toISTDisplay(l.call_date),
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8," + [headers, ...rows].map((r) => r.join(",")).join("\n");
    const link = document.createElement("a");
    link.setAttribute("href", encodeURI(csvContent));
    link.setAttribute("download", "sales_call_logs.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const columns = [
    { key: "employee_name", label: "Employee", render: (r) => r.employee_name || "—" },
    { key: "customer_name", label: "Customer", render: (r) => r.customer_name || "-" },
    { key: "phone", label: "Phone" },
    { key: "source", label: "Source", render: (r) => r.source || "-" },
    {
      key: "call_status",
      label: "Status",
      render: (r) => (
        <span className={`status-pill status-${(r.call_status || "").toLowerCase().replace(/\s+/g, "-")}`}>
          {r.call_status || "—"}
        </span>
      ),
    },
    { key: "packages", label: "Package", render: (r) => r.packages || "-" },
    {
      key: "notes",
      label: "Notes",
      render: (r) => (r.notes ? (r.notes.length > 40 ? `${r.notes.slice(0, 40)}...` : r.notes) : "-"),
    },
    { key: "call_date", label: "Date & Time", render: (r) => toISTDisplay(r.call_date) },
  ];

  return (
    <div className="target-achievements-admin-page">
      <div className="mytarget-header">
        <div>
          <h2>
            <FaPhone /> Sales Call Logs
          </h2>
          <p>Every call logged by your Telesales &amp; Sales Executive team, in one place</p>
        </div>
        <button className="btn-secondary" onClick={handleExport}>
          <FaDownload /> Export CSV
        </button>
      </div>

      {/* KPI CARDS */}
      <div className="kpi-mini-grid">
        <KpiCard label="Total Calls" value={stats.total} icon={<FaPhone />} accent="indigo" />
        <KpiCard label="Interested" value={stats.interested} icon={<FaCheckCircle />} accent="green" />
        <KpiCard label="Not Interested" value={stats.notInterested} icon={<FaTimesCircle />} accent="red" />
        <KpiCard label="Followups" value={stats.followup} icon={<FaClock />} accent="amber" />
      </div>

      {/* FILTERS */}
      <div className="filters-bar">
        <input
          placeholder="Search customer, phone, source, employee..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
          <option value="">All employees</option>
          {employees.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
      </div>

      <DataTable
        columns={columns}
        data={pagedLogs}
        loading={loading}
        emptyIcon="📞"
        emptyText="No call logs found"
        page={page}
        limit={limit}
        total={filteredLogs.length}
        onPageChange={setPage}
      />
    </div>
  );
}

export default SalesCallLogs;
